import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface ConfigRow {
  key: string;
  value: unknown;
  description: string | null;
  updated_at: string;
}

const FRIENDLY: Record<string, string> = {
  signup_bonus_cents: "Signup bonus (cents)",
  referral_points: "Referral points",
  spin_daily_cap: "Spin daily cap",
  spin_global_cash_cap_cents: "Spin global cash cap / 24h (cents)",
  withdrawal_daily_cap_cents: "Withdrawal daily cap (cents)",
  kyc_required_above_cents: "KYC required above (cents)",
};

const AdminRewards = () => {
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("reward_config")
      .select("*")
      .order("key");
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((data ?? []) as ConfigRow[]);
    const next: Record<string, string> = {};
    (data ?? []).forEach((r: ConfigRow) => {
      next[r.key] = JSON.stringify(r.value);
    });
    setDrafts(next);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (key: string) => {
    setBusy(key);
    try {
      const parsed = JSON.parse(drafts[key]);
      const { error } = await supabase.rpc("admin_set_reward_config", {
        _key: key,
        _value: parsed,
      });
      if (error) throw error;
      toast.success(`${FRIENDLY[key] ?? key} updated`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Reward control panel</h1>
        <p className="text-sm text-muted-foreground">
          Tune signup bonuses, spin caps, and withdrawal limits without redeploying.
        </p>
      </div>

      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.key} className="p-4 space-y-2">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <Label className="text-sm font-medium">
                {FRIENDLY[r.key] ?? r.key}
                <span className="ml-2 font-mono text-xs text-muted-foreground">{r.key}</span>
              </Label>
              <span className="text-[10px] text-muted-foreground">
                Updated {new Date(r.updated_at).toLocaleString()}
              </span>
            </div>
            {r.description && (
              <div className="text-xs text-muted-foreground">{r.description}</div>
            )}
            <div className="flex gap-2">
              <Input
                value={drafts[r.key] ?? ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
                className="font-mono text-sm"
              />
              <Button onClick={() => save(r.key)} disabled={busy === r.key}>
                {busy === r.key ? "Saving…" : "Save"}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminRewards;
